import { AccountStatus } from '../accounts-overview/accounts-overview.types';

//REV-21361 — Types of the account detail shell (abcp-account-workspace).

export interface AccountHeader {
    number: string;   // account number, e.g. "1022" — never used for arithmetic
    name: string;
}

/** Amount with its own currency code; the code may be absent. */
export interface Money {
    amount: number;
    currency?: string | null;
}

export interface AccountReconciliation {
    erpBalance?: Money | null;
    documentBalance?: Money | null;
    documentBalanceEditable?: boolean;   // shows the pencil
    /**
     * REV-22005 — `'HUMAN'` shows the "von Hand erfasst" hint beside the value (and the difference
     * against the ERP figure, which no status chip carries for a hand-entered value).
     */
    documentBalanceOrigin?: 'MACHINE' | 'HUMAN' | null;
    status: AccountStatus;
    difference?: Money | null;           // signed
    /** REV-22302 — what the AI does with the account's documents; absent = the plain value. */
    aiReading?: AccountAiReading | null;
    /** Why the account shows its chip — the line under it; absent = nothing to say. */
    reason?: AccountReasonView | null;
}

/** The facts the line under the chip names. Amounts in the card's display sign, like the balances. */
export interface AccountReasonView {
    /** `DOCUMENT_MISSING`, `DOCUMENT_DATE_MISMATCH`, `RECONCILED_WITH_DOCUMENT`, … */
    code: string;
    /** The document kind the line names, e.g. `BANK_STATEMENT`. */
    kind?: string | null;
    /** ISO date the line names — the proof's balance date. */
    date?: string | null;
    amount?: Money | null;
    /** Contradicting proofs: each kind with the value it says. */
    conflicting?: { kind: string; amount: Money }[];
    /** Securities: which read value matched. */
    basis?: 'MARKET_VALUE' | 'COST' | null;
    /** ISO date — the closing's balance-sheet date, which a proof dated elsewhere is compared with. */
    balanceSheetDate?: string | null;
}

export type AccountAiReadingState = 'READING' | 'RECONCILING' | 'VALUE' | 'REFUSED' | 'OPEN' | 'MANUAL';

export interface AccountAiReading {
    state: AccountAiReadingState;
    documentName?: string;
    /** VALUE: the read value; MANUAL: the hand-entered one. */
    amount?: Money | null;
    asOfDate?: string;
    quote?: string;
    line?: string;
    /** REFUSED / OPEN: the pipeline reason (keys as on the document page), or that the run took no value. */
    reason?: string;
    /** READING: the step the document is in (`SCAN`, `TEXT`, `READING`), as in the documents list. */
    step?: string;
}

/** Prefill of the manual-entry row; empty fields when freshly opened. */
export interface ManualBalanceEntry {
    amount?: number | null;
    reference?: string | null;
}

/** Prefill of the review dialog; empty field when freshly opened. */
export interface ReviewDialogState {
    comment?: string | null;
}

export type PaneKind = 'ledger' | 'documents' | 'comments' | 'todos' | 'adjustments' | 'activityLog';

export interface WorkspacePane {
    id: string;
    kind: PaneKind;
    count?: number | null;   // not delivered yet
}

export interface ManualBalanceEntryValue {
    /** `null` = the field was left empty: take the document value away. */
    amount: number | null;
    reference: string;
}

export interface PaneKindChange {
    id: string;
    kind: PaneKind;
}

/** One entry of the kind select; the label is translated by the view. */
export interface PaneKindOption {
    id: PaneKind;
    label: string;
}
